(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('lb-motion-ready');

  const ready = () => {
    const main = document.querySelector('main[data-template]');
    if (!main) return;

    if (main.dataset.template.startsWith('index')) {
      document.body.classList.add('lb-home');
    }

    setupHeader();
    setupReveals();
    setupParallax();
    setupCompactCollection(main);
    setupBundleClarity(main);
    setupCartFeedback();
  };

  const setupHeader = () => {
    const header = document.querySelector('.header-section');
    if (!header) return;

    let scheduled = false;
    const update = () => {
      scheduled = false;
      const scrolled = window.scrollY > 48;
      document.body.classList.toggle('lb-scrolled', scrolled);
      window.setTimeout(() => {
        const headerComponent = document.querySelector('header-component');
        const headerGroup = document.querySelector('#header-group');
        if (headerComponent) {
          document.body.style.setProperty('--header-height', headerComponent.offsetHeight + 'px');
        }
        if (headerGroup) {
          document.body.style.setProperty('--header-group-height', headerGroup.offsetHeight + 'px');
        }
      }, 340);
    };
    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  };

  const setupReveals = () => {
    const items = [...document.querySelectorAll('[data-lb-reveal]')];
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
    items.forEach((item) => observer.observe(item));
  };

  const setupParallax = () => {
    if (reduceMotion) return;
    const floatImage = document.querySelector('.lb-hero-float-image');
    const storyImage = document.querySelector('.lb-emotional-image img');
    if (!floatImage && !storyImage) return;

    let scheduled = false;
    const update = () => {
      scheduled = false;
      const y = window.scrollY;
      if (floatImage && y < window.innerHeight * 1.3) {
        floatImage.style.transform = 'translate3d(0,' + Math.min(18, y * 0.022) + 'px,0)';
      }
      if (storyImage) {
        const rect = storyImage.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - (rect.top + rect.height / 2)) / window.innerHeight));
        storyImage.style.transform = 'scale(1.035) translate3d(0,' + (progress * 10) + 'px,0)';
      }
    };
    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  };

  const setupCompactCollection = (main) => {
    if (!main.dataset.template.startsWith('collection')) return;
    const cards = main.querySelectorAll('product-card, .product-grid__card');
    const grid = main.querySelector('.product-grid');
    if (!grid || cards.length < 1 || cards.length > 4) return;

    main.classList.add('lb-compact-collection');
    if (main.querySelector('.lb-collection-set-callout')) return;

    const callout = document.createElement('section');
    callout.className = 'lb-collection-set-callout';
    callout.setAttribute('aria-label', 'Build a Little Birdie matching set');
    callout.innerHTML = '<div><h2>Make it a matching round.</h2><p>Choose a design, then select a size for each golfer. Two and three-polo sets receive automatic bundle savings.</p></div><a href="#ResultsList">Choose a design</a>';
    grid.insertAdjacentElement('afterend', callout);
  };

  const setupBundleClarity = (main) => {
    if (!main.dataset.template.startsWith('product')) return;

    let attempts = 0;
    const enhance = () => {
      attempts += 1;
      const inputs = [...document.querySelectorAll('input[name^="kaching-bundles-deal"]')];
      if (inputs.length < 2) return false;

      const labelFor = (input) => input.closest('label') || input.parentElement;
      const labels = inputs.map(labelFor).filter(Boolean);
      let root = labels[0];
      while (root && root !== document.body && !labels.every((label) => root.contains(label))) {
        root = root.parentElement;
      }

      if (root && root !== document.body && !document.querySelector('.lb-matching-builder-intro')) {
        const intro = document.createElement('div');
        intro.className = 'lb-matching-builder-intro';
        intro.innerHTML = '<strong>Build your matching set</strong><p>Start with one polo, or choose a two or three-polo set and select a size for every golfer. Bundle totals below include every polo in the set.</p>';
        root.insertAdjacentElement('beforebegin', intro);
      }

      const descriptions = [
        { match: /Ace|Standard price/i, text: 'Single polo' },
        { match: /Birdie|save 15/i, text: 'Two-polo matching set' },
        { match: /Par|save 20/i, text: 'Three-polo family set' }
      ];
      labels.forEach((label) => {
        if (label.querySelector('.lb-bundle-clarity')) return;
        const item = descriptions.find((description) => description.match.test(label.textContent || ''));
        if (!item) return;
        const clarity = document.createElement('span');
        clarity.className = 'lb-bundle-clarity';
        clarity.textContent = item.text;
        label.appendChild(clarity);
      });

      if (!document.body.dataset.lbBundleDefaulted) {
        const single = inputs.find((input) => /Ace|Standard price/i.test((labelFor(input)?.textContent || '')));
        if (single && !single.checked) {
          document.body.dataset.lbBundleDefaulted = 'true';
          single.click();
        } else if (single) {
          document.body.dataset.lbBundleDefaulted = 'true';
        }
      }

      if (!document.querySelector('.lb-product-price-note')) {
        const title = main.querySelector('h1');
        const scope = title?.parentElement;
        const price = scope?.querySelector('[class*="price"]');
        if (price) {
          const note = document.createElement('p');
          note.className = 'lb-product-price-note';
          note.textContent = 'Single polos from $24.99. Matching-set totals update below.';
          price.insertAdjacentElement('afterend', note);
        }
      }
      if (root && root !== document.body) {
        root.querySelectorAll('*').forEach((element) => {
          element.childNodes.forEach((node) => {
            if (node.nodeType !== Node.TEXT_NODE || !node.nodeValue) return;
            node.nodeValue = node.nodeValue
              .replace(/system\.original_price\s*/g, '')
              .replace(/system\.price\s*/g, '');
          });
        });
      }
      return true;
    };

    if (enhance()) return;
    const observer = new MutationObserver(() => {
      if (enhance()) observer.disconnect();
      if (attempts > 30) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 12000);
  };

  const setupCartFeedback = () => {
    const cartCount = document.querySelector('.cart-bubble__text-count');
    const bubble = document.querySelector('.cart-bubble');
    if (!cartCount || !bubble) return;
    let previous = cartCount.textContent;
    const observer = new MutationObserver(() => {
      if (cartCount.textContent === previous) return;
      previous = cartCount.textContent;
      bubble.classList.remove('lb-cart-pulse');
      void bubble.offsetWidth;
      bubble.classList.add('lb-cart-pulse');
    });
    observer.observe(cartCount, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
